/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import LoaderButton from './LoaderButton';

/**
 * This component serves as a replacement for the legacy ConfirmModal component.
 * It is part of our ongoing migration effort to move away from handling forms via an
 * abstracted confirm object, ensuring a more straightforward and maintainable codebase.
 */

function ConfirmationModal({
  size,
  data,
  title,
  message,
  renderMessageComponent,
  itemLabel,
  buttonLabel,
  buttonLoadingLabel,
  buttonVariant,
  buttonDisabled,
  show,
  cancelButtonLabel,
  handleSubmit,
  setShow,
}) {
  const [disabled, setDisabled] = useState(buttonDisabled);
  const { t } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const onSubmit = async (values) => {
    setSubmitting(true);
    await handleSubmit(values);
    setSubmitting(false);
  };

  useEffect(() => {
    setSubmitting(false);
  }, [show]);

  useEffect(() => {
    setDisabled(buttonDisabled);
  }, [buttonDisabled]);

  return (
    <Modal
      centered
      id="confirm-modal"
      backdrop="static"
      show={show}
      size={size}
      aria-labelledby="confirm-modal-title"
    >
      <Modal.Header closeButton={!isSubmitting} onHide={() => setShow(false)}>
        <Modal.Title as="h3" id="confirm-modal-title">
          {title || t('common:buttons.saveChanges')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderMessageComponent ? (
          renderMessageComponent({ data, setDisabled })
        ) : (
          <p className="text-lg text-info">
            {data && itemLabel
              ? t('common:form.messages.confirm.save', { name: itemLabel })
              : message || t('common:form.messages.confirm.default')}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={isSubmitting} variant="outline-light" onClick={() => setShow(false)}>
          {cancelButtonLabel || t('common:buttons.cancel')}
        </Button>
        <LoaderButton
          disabled={disabled}
          loadingLabel={buttonLoadingLabel || undefined}
          loading={isSubmitting}
          variant={buttonVariant || undefined}
          onClick={() => onSubmit(data)}
        >
          {buttonLabel || t('common:buttons.save')}
        </LoaderButton>
      </Modal.Footer>
    </Modal>
  );
}

ConfirmationModal.propTypes = {
  /**
   * The size of the modal
   */
  size: PropTypes.string,
  /**
   * The data object
   */
  data: PropTypes.any,
  /**
   * The confirm modal title
   */
  title: PropTypes.string,
  /**
   * The confirm modal description
   */
  message: PropTypes.string,
  /**
   * The confirm modal message component
   */
  renderMessageComponent: PropTypes.func,
  /**
   * The name of the item to be confirmed
   */
  itemLabel: PropTypes.string,
  /**
   * The button label
   */
  buttonLabel: PropTypes.string,
  /**
   * The button loading label
   */
  buttonLoadingLabel: PropTypes.string,
  /**
   * The button style
   */
  buttonVariant: PropTypes.string,
  /**
   * The button disabled boolean
   */
  buttonDisabled: PropTypes.bool,
};

export default ConfirmationModal;
